export interface RouteConfig {
  path: string;
  title?: string;
  children?: RouteConfig[];
  [key: string]: any;
}

const ROOT_PATH = '/';
const EMPTY_PATH = '';

/**
 * Recursively searches through routes to find a matching path and extract its title
 * @param pathname - The current pathname from useLocation
 * @param routes - Array of route configurations
 * @param parentPath - Parent path for building the full path (used in recursion)
 * @returns The title of the matching route, or empty string if no match
 */
export const getTitleFromRoutes = (
  pathname: string,
  routes: RouteConfig[],
): string => {
  // Handle root path
  if (pathname === ROOT_PATH) {
    // Check for root route, then look for its empty-path child
    for (const route of routes) {
      const title = getDefaultChildTitle(route);
      if (title) {
        return title;
      }
    }

    return '';
  }

  // Remove leading slash and process segments
  const segments = normalizePathname(pathname);

  return findInRoutes(segments, 0, routes);
};

function normalizePathname(pathname: string): string[] {
  const trimmed = pathname.startsWith(ROOT_PATH) ? pathname.slice(1) : pathname;

  return trimmed.split('/');
}

function getDefaultChildTitle(route: RouteConfig): string {
  if (route.path !== ROOT_PATH || !route.children) {
    return '';
  }

  for (const child of route.children) {
    if (child.path === EMPTY_PATH && child.title) {
      return child.title;
    }
  }

  return '';
}

function getEmptyPathChildTitle(route: RouteConfig): string {
  if (!route.children) {
    return '';
  }

  for (const child of route.children) {
    if (child.path === EMPTY_PATH && child.title) {
      return child.title;
    }
  }

  return '';
}

function splitRoutePath(routePath: string): string[] {
  const normalized = routePath.startsWith(ROOT_PATH)
    ? routePath.slice(1)
    : routePath;

  return normalized.split('/');
}

function isParamSegment(segment: string): boolean {
  return segment.startsWith(':');
}

function isSegmentMatch(routeSegment: string, inputSegment: string): boolean {
  return routeSegment === inputSegment || isParamSegment(routeSegment);
}

/**
 * Find title in routes by matching segments
 */
function findInRoutes(
  segments: string[],
  startIndex: number,
  routes: RouteConfig[],
): string {
  if (startIndex >= segments.length) {
    return '';
  }

  for (const route of routes) {
    // Handle root path - always check its children if it has any
    if (route.path === ROOT_PATH && route.children) {
      const result = findInRoutes(segments, startIndex, route.children);
      if (result) {
        return result;
      }
    }

    // Skip empty paths or root path
    if (route.path === EMPTY_PATH || route.path === ROOT_PATH) {
      continue;
    }

    // Check if this route matches the current segment(s)
    const routeSegments = splitRoutePath(route.path);

    if (routeSegments.length === 0) {
      continue;
    }

    // Check if we have enough segments to match
    if (startIndex + routeSegments.length > segments.length) {
      continue;
    }

    let isMatch = true;
    for (let i = 0; i < routeSegments.length; i++) {
      const routeSeg = routeSegments[i];
      const inputSeg = segments[startIndex + i];
      if (!isSegmentMatch(routeSeg, inputSeg)) {
        isMatch = false;
        break;
      }
    }

    if (!isMatch) {
      continue;
    }

    const nextIndex = startIndex + routeSegments.length;
    const hasMoreAfterMatch = nextIndex < segments.length;

    // If there are more segments after this route
    if (hasMoreAfterMatch && route.children) {
      const result = findInRoutes(segments, nextIndex, route.children);
      if (result) {
        return result;
      }
    }

    // If no more segments and route has title
    if (!hasMoreAfterMatch && route.title) {
      return route.title;
    }

    // If no more segments but route has empty-path child with title
    if (!hasMoreAfterMatch) {
      const childTitle = getEmptyPathChildTitle(route);
      if (childTitle) {
        return childTitle;
      }
    }
  }

  return '';
}
